"use client";

import type { MutationOptions } from "@tanstack/react-query";
import { useMutation } from "@tanstack/react-query";
import gql from "graphql-tag";
import type {
  CanvasSchedulerResponse,
  CreateCanvasPhoneAppointmentInput,
} from "@rmh/defs";
import type { GeneratedMutation } from "@aws-amplify/api-graphql";
import { fetchGraphQLClient } from "../../../amplify/fetchGraphQLClient";

interface CreateCanvasPhoneAppointmentVariables {
  input: CreateCanvasPhoneAppointmentInput;
}

interface CreateCanvasPhoneAppointmentResponse {
  data?: {
    createCanvasPhoneAppointment: CanvasSchedulerResponse;
  };
}

export const createCanvasPhoneAppointmentMutation = gql`
  mutation createCanvasPhoneAppointment(
    $input: CreateCanvasPhoneAppointmentInput!
  ) {
    createCanvasPhoneAppointment(input: $input) {
      success
      error {
        type
        message
        details
      }
      data {
        appointmentId
        canvasAppointmentId
        canvasPractitionerId
        appointmentDateTime
        timestamp
      }
    }
  }
`.loc?.source.body as GeneratedMutation<
  CreateCanvasPhoneAppointmentVariables,
  {
    createCanvasPhoneAppointment: CanvasSchedulerResponse;
  }
>;

export const useCreateCanvasPhoneAppointment = (
  options: MutationOptions<
    CreateCanvasPhoneAppointmentResponse,
    Error,
    CreateCanvasPhoneAppointmentVariables
  > = {}
) => {
  const { mutateAsync, isPending } = useMutation<
    CreateCanvasPhoneAppointmentResponse,
    Error,
    CreateCanvasPhoneAppointmentVariables
  >({
    ...options,
    mutationFn: async (variables) =>
      fetchGraphQLClient(createCanvasPhoneAppointmentMutation, variables),
  });

  const createCanvasPhoneAppointment = async (
    input: CreateCanvasPhoneAppointmentInput
  ) => {
    try {
      const response = await mutateAsync({ input });

      return response?.data?.createCanvasPhoneAppointment;
    } catch (error) {
      console.error("Failed to create Canvas phone appointment", error);
      throw error;
    }
  };

  return {
    createCanvasPhoneAppointment,
    isPending,
  };
};
